<template>
  <div id="forms">
    <client-only>
      <form-modal-v2
        v-if="activeForm && formattedActiveQuestions"
        :form="activeForm"
        :questions="formattedActiveQuestions"
        @close="handleCloseModal"
      />
    </client-only>
    <Breadcrumbs
      :crumbs="[
        { name: globals.info.school_name, link: `${pathPrefix}/` },
        { name: 'Forms', link: `${pathPrefix}/forms` },
      ]"
    />
    <div
      ref="container"
      class="page-container"
    >
      <div class="forms-header">
        <h2 class="forms-title">
          {{ $t('forms.forms') }}
        </h2>
        <div
          v-if="showFormsList"
          class="forms-search"
        >
          <label
            for="forms-search-input"
            class="sr-only"
            >{{ $t('forms.search') }}</label
          >
          <text-input
            id="forms-search-input"
            v-model="search"
            type="search"
            :has-errors="false"
            :placeholder="$t('pages.shared.searchPlaceholder')"
            @update:model-value="handleSearch"
          />
          <img
            src="../../../assets/search.svg"
            class="search-icon"
            alt=""
          />
        </div>
      </div>
      <EmptyFormsList v-if="isEmptyFormsList" />
      <UnavailableForm
        v-else-if="isUnavailableForm"
        @close-unavailable="isUnavailableForm = false"
      />
      <FormsList
        v-else
        :forms="forms"
        @open="handleOpenFormModal($event, 'click')"
        @open-via-keyboard="handleOpenFormModal($event, 'keyboard')"
      />
      <p
        aria-live="polite"
        class="sr-only"
      >
        {{ ariaLiveFormCount }} {{ $t('forms.formsFound') }}
      </p>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import axios from 'axios';
import { useMainStore } from '../../../stores/mainStore';
import { useTranslationStore } from '../../../stores/translationStore';
import get from 'lodash/get';
import debounce from 'lodash/debounce';
import concat from 'lodash/concat';
import camelCase from 'lodash/camelCase';
import isPlainObject from 'lodash/isPlainObject';
import JsonApiResponseConverter from 'json-api-response-converter';
import FormModalV2 from '../../../components/pages/forms/FormModalV2.vue';
import Breadcrumbs from '../../../components/global/Breadcrumbs.vue';
import TextInput from '../../../components/global/Forms/TextInput.vue';
import UnavailableForm from '../../../components/pages/forms/views/UnavailableForm.vue';
import FormsList from '../../../components/pages/forms/views/FormsList.vue';
import EmptyFormsList from '../../../components/pages/forms/views/EmptyFormsList.vue';
import { useNuxtApp, useRoute, useAsyncData, createError, useHead, useRuntimeConfig } from '#imports';

defineOptions({ name: 'Forms' });

const mainStore = useMainStore();
const translationStore = useTranslationStore();
const route = useRoute();
const { $axios } = useNuxtApp();
const runtimeConfig = useRuntimeConfig();

const formId = route.params.id || false;
const formUuid = route.params.uuid || '';
const secondaryOrgId = computed(() => mainStore.getSecondaryOrgId);

const { data: formsInitialData } = await useAsyncData(`forms-${formId || 'list'}-${formUuid}`, async () => {
  try {
    const locale = translationStore.locale;
    const featureFlags = mainStore.featureFlags || {};

    const hasFormsEnabled = featureFlags.forms_service_enabled || featureFlags.vue_migration_forms_enabled;

    if (!hasFormsEnabled) {
      throw createError({ statusCode: 404, message: 'Page not found' });
    }

    const orgParam = {
      secondary_organization_id: secondaryOrgId.value,
    };
    const displayParams = {
      sort: 'name',
      scope: 'visible_on_school_website',
    };

    // using an axios instance causes generated pagination links to be built with incorrect domains
    const response = await axios.get(`${runtimeConfig.public.FORMS_SERVICE_API_URL}api/v1/forms`, {
      params: {
        locale: locale !== 'en' ? locale : undefined,
        ...displayParams,
        ...orgParam,
      },
    });

    const unfilteredForms = response.data.data;
    const unfilteredPaginationLinks = response.data.links;
    let unavailableForm = false;
    const emptyFormsList = response.data.data.length === 0;
    let queryForm = null;
    let queryQuestions = '';
    let queryChoices = [];
    const id = route.params.id || false;
    const uuid = route.params.uuid || '';

    if (id) {
      queryForm = unfilteredForms.find((form) => form.id === id);
      if (!queryForm) {
        try {
          const {
            data: { data: directForm },
          } = await $axios.get(`${runtimeConfig.public.FORMS_SERVICE_API_URL}api/v1/forms/${id}/${uuid}`, {
            params: {
              locale: locale !== 'en' ? locale : undefined,
              ...orgParam,
            },
          });
          if (directForm.attributes.status === 'SURVEY_STATUS_ACTIVE') {
            queryForm = directForm;
          } else {
            throw new Error('Form not currently active');
          }
        } catch (err) {
          unavailableForm = true;
          console.error(err);
        }
      }

      if (queryForm) {
        const questionsResponse = await $axios.get(
          `${runtimeConfig.public.FORMS_SERVICE_API_URL}api/v1/forms/${queryForm.id}/elements`,
          {
            params: {
              locale: locale !== 'en' ? locale : undefined,
              include: 'element_choices,assets',
              ...orgParam,
            },
          },
        );
        queryQuestions = new JsonApiResponseConverter(questionsResponse.data).formattedResponse;

        queryChoices = queryQuestions.reduce(
          (map, { element_choices, id: qId }) => ({
            ...map,
            [qId]: element_choices,
          }),
          {},
        );
      }
    }

    return {
      unfilteredForms,
      orgParam,
      displayParams,
      unfilteredPaginationLinks,
      activeForm: queryForm,
      activeQuestions: queryQuestions,
      activeChoices: queryChoices,
      unavailableForm,
      emptyFormsList,
    };
  } catch (e) {
    console.error(e);
    const status = e?.statusCode || e?.response?.status;
    throw createError({
      statusCode: status || 500,
      message: status ? e.message : 'Something went wrong',
    });
  }
});

const unfilteredForms = ref([]);
const orgParam = ref({});
const displayParams = ref({});
const unfilteredPaginationLinks = ref({});
const activeForm = ref(null);
const activeQuestions = ref('');
const activeChoices = ref([]);
const isUnavailableForm = ref(false);
const isEmptyFormsList = ref(false);

const forms = ref([]);
const search = ref('');
const paginationLinks = ref({});
const scrollTop = ref(0);
const isLoading = ref(false);
const container = ref(null);

const d = formsInitialData.value;
if (d) {
  unfilteredForms.value = d.unfilteredForms;
  orgParam.value = d.orgParam;
  displayParams.value = d.displayParams;
  unfilteredPaginationLinks.value = d.unfilteredPaginationLinks;
  activeForm.value = d.activeForm;
  activeQuestions.value = d.activeQuestions;
  activeChoices.value = d.activeChoices;
  isUnavailableForm.value = d.unavailableForm;
  isEmptyFormsList.value = d.emptyFormsList;
  forms.value = d.unfilteredForms;
  paginationLinks.value = d.unfilteredPaginationLinks;
}

useHead({
  title: 'Forms',
});

const globals = computed(() => mainStore.globals);
const pathPrefix = computed(() => mainStore.pathPrefix);
const locale = computed(() => translationStore.locale);

const ariaLiveFormCount = computed(() => get(forms.value, 'length'));
const hasSearch = computed(() => search.value && search.value.trim().length > 0);
const showFormsList = computed(() => !isUnavailableForm.value && !isEmptyFormsList.value);

function camelCaseKeys(object) {
  return Object.entries(object).reduce((obj, [key, value]) => {
    let localValue = value;
    if (isPlainObject(value)) {
      localValue = camelCaseKeys(value);
    }
    return { ...obj, [camelCase(key)]: localValue };
  }, {});
}

const formattedActiveQuestions = computed(() => {
  if (!Array.isArray(activeQuestions.value)) return activeQuestions.value;
  return activeQuestions.value.map((question) => ({
    ...camelCaseKeys(question),
  }));
});

async function getSearchResults(query) {
  const response = await $axios.get(`${runtimeConfig.public.FORMS_SERVICE_API_URL}api/v1/forms`, {
    params: {
      locale: locale.value !== 'en' ? locale.value : undefined,
      ...displayParams.value,
      ...orgParam.value,
      query,
    },
  });
  return response.data;
}

async function performSearch(value) {
  let searchResults;
  if (hasSearch.value) {
    searchResults = await getSearchResults(value);
  }
  forms.value = !hasSearch.value ? unfilteredForms.value : searchResults.data;
  paginationLinks.value = !hasSearch.value ? unfilteredPaginationLinks.value : searchResults.links;
}

const handleSearch = debounce(performSearch, 500);

function handleScroll() {
  scrollTop.value = window.pageYOffset;
  const el = container.value;
  const { clientHeight } = document.documentElement;
  if (scrollTop.value + clientHeight > el.clientHeight) {
    addMoreForms();
  }
}

async function addMoreForms() {
  if (!paginationLinks.value.next) return;
  try {
    if (isLoading.value) return;
    isLoading.value = true;
    const { data } = await $axios.get(`${paginationLinks.value.next}`);
    isLoading.value = false;
    const nextFormsPage = data.data;
    if (hasSearch.value) {
      paginationLinks.value = data.links;
      forms.value = concat(forms.value, nextFormsPage);
    } else {
      unfilteredPaginationLinks.value = data.links;
      paginationLinks.value = unfilteredPaginationLinks.value;
      unfilteredForms.value = concat(unfilteredForms.value, nextFormsPage);
      forms.value = unfilteredForms.value;
    }
  } catch (e) {
    console.error(e);
  }
}

async function handleOpenFormModal(form, actionType) {
  try {
    const questionsResponse = await $axios.get(
      `${runtimeConfig.public.FORMS_SERVICE_API_URL}api/v1/forms/${form.id}/elements`,
      {
        params: {
          locale: locale.value !== 'en' ? locale.value : undefined,
          include: 'element_choices',
          secondary_organization_id: secondaryOrgId.value,
        },
      },
    );

    activeQuestions.value = new JsonApiResponseConverter(questionsResponse.data).formattedResponse;

    activeChoices.value = activeQuestions.value.reduce(
      (map, { element_choices, id: qId }) => ({
        ...map,
        [qId]: element_choices,
      }),
      {},
    );
    activeForm.value = { ...form, openedBy: actionType };

    // return navigateTo causes a rerender and makes the form modal opening jump.
    history.pushState(null, null, `${pathPrefix.value}/forms/${form.id}`);
  } catch (err) {
    console.error(err);
  }
}

function formReset() {
  activeForm.value = null;
  activeQuestions.value = '';
  activeChoices.value = [];
}

function handleCloseModal() {
  document.querySelector('body').style.overflow = 'visible';
  formReset();
  return navigateTo({ path: `${pathPrefix.value}/forms` });
}

onMounted(() => {
  document.addEventListener('scroll', handleScroll);
});

onBeforeUnmount(() => {
  if (import.meta.client) {
    document.removeEventListener('scroll', handleScroll);
  }
});
</script>

<style scoped lang="scss">
#forms {
  .forms-header {
    display: flex;
    justify-content: space-between;
    margin: 0 auto 36px;
    width: 90%;
  }
  .forms-search {
    position: relative;
    width: clamp(182px, 70%, 250px);
    height: 40px;
    margin-left: 5px;
    :deep(.form-input) {
      input {
        box-sizing: border-box;
        border-radius: 4px 4px 0 0;
      }
    }
    .search-icon {
      position: absolute;
      top: 5px;
      right: 12px;
    }
  }
}
.sr-only {
  clip: rect(1px, 1px, 1px, 1px);
  clip-path: inset(50%);
  height: 1px;
  width: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
}
</style>
